import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core'
import { FormControl, FormGroup, Validators } from '@angular/forms'
import { ActivatedRoute, Router } from '@angular/router'

import { takeUntil } from 'rxjs'

import { ApiService } from '@ti/core/api'
import { AuthSession, LogoutStrategy } from '@ti/core/auth/services'
import { parseInternalReturnUrl } from '@ti/core/auth/utils'
import { BrowserLocalStorage } from '@ti/core/common/storage'
import { injectDestroy$ } from '@ti/core/common/utils'
import { LanguageService } from '@ti/core/ui-kit/i18n'

import { AuthenticatorCodeDialogComponent } from '../components/authenticator-code-dialog'
import { AuthenticatorSetupDialogComponent } from '../components/authenticator-setup-dialog'

@Component({
  selector: 'app-auth-base-sign-in',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AuthenticatorCodeDialogComponent, AuthenticatorSetupDialogComponent],
  template: `
    <app-authenticator-code-dialog
      #authenticatorCodeDialogRef
      (confirmed)="onMfaStepCompleted($event)"
      (cancelClicked)="signingIn.set(false)"
    ></app-authenticator-code-dialog>

    <app-authenticator-setup-dialog #authenticatorSetupDialogRef></app-authenticator-setup-dialog>
  `,
})
export class BaseSignInComponent implements OnInit {
  @ViewChild('authenticatorCodeDialogRef')
  protected readonly authenticatorCodeDialog?: AuthenticatorCodeDialogComponent

  @ViewChild('authenticatorSetupDialogRef')
  protected readonly authenticatorSetupDialog?: AuthenticatorSetupDialogComponent

  protected readonly signingIn = signal(false)
  protected readonly errorMessage = signal('')
  protected readonly api = inject(ApiService)
  protected readonly authSession = inject(AuthSession)
  protected readonly logoutStrategy = inject(LogoutStrategy)
  protected readonly router = inject(Router)
  protected readonly route = inject(ActivatedRoute)
  protected readonly localStorage = inject(BrowserLocalStorage)
  protected readonly languageService = inject(LanguageService)
  protected readonly destroy$ = injectDestroy$()

  protected readonly signInForm = new FormGroup({
    username: new FormControl<string>('', [Validators.required]),
    password: new FormControl<string>('', [Validators.required]),
    rememberMe: new FormControl<boolean>(false),
  })

  protected readonly USERNAME_STORAGE_KEY = 'SAVED_USERNAME'
  protected readonly returnUrl =
    parseInternalReturnUrl(this.route.snapshot.queryParams['returnUrl']) ?? '/'

  public async ngOnInit() {
    if (await this.authSession.isLoggedIn()) {
      // The same validated target the sign-in itself returns to, so one page never gives two answers.
      await this.router.navigateByUrl(this.returnUrl)
      return
    }

    const savedUsername = this.localStorage.getItem(this.USERNAME_STORAGE_KEY)
    if (savedUsername) {
      this.signInForm.get('username')?.setValue(savedUsername)
    }

    // Leave sign-in page after sign-in is completed
    this.authSession.state.onSignedIn$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      return this.router.navigateByUrl(this.returnUrl)
    })
  }

  public async onSignIn() {
    if (this.signInForm.invalid || this.signingIn()) {
      return this.signInForm.markAsTouched()
    }

    try {
      this.signingIn.set(true)
      const data = this.signInForm.value
      const result = await this.authSession.signIn(
        this.prepareUsername(data.username || ''),
        data.password || '',
      )

      if (result) {
        switch (result.nextStep.signInStep) {
          case 'CONFIRM_SIGN_IN_WITH_TOTP_CODE':
            return this.authenticatorCodeDialog?.open()

          case 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP':
            return this.authenticatorSetupDialog?.open()

          case 'DONE':
            console.log('Login successful without MFA challenge.')
            break
        }
      }

      await this.processSignInResult(result)
    } catch (e) {
      console.error(`Unable to sign in: `, e)
      this.signingIn.set(false)
      await this.processSignInResult(undefined)
    }
  }

  protected async onMfaStepCompleted(result: any) {
    await this.processSignInResult(result)
    this.signingIn.set(false)
  }

  protected async processSignInResult(result: any) {
    let signedIn = false
    let errorMessage = 'sign-in.error-message'
    const data = this.signInForm.value

    if (result?.isSignedIn) {
      let isActive = false
      try {
        isActive = await this.checkIsUserActive()
        if (isActive) {
          this.errorMessage.set('') // Clear error message on success
          console.debug('Sign-in successful!')

          this.trackLogin()

          if (data.rememberMe && data.username) {
            this.localStorage.setItem(this.USERNAME_STORAGE_KEY, data.username)
          }
          signedIn = true
        } else {
          console.debug('User is inactive')
          signedIn = false
          // delay required due to page reload after sign-out but let user read error message
          setTimeout(() => this.logoutStrategy.signOut(), 3500)
        }
      } catch (e) {
        console.error(e)
        errorMessage = 'sign-in.general-error-message'
        setTimeout(() => this.logoutStrategy.signOut(), 3500)
      }
    }

    this.signingIn.set(false)

    if (!signedIn) {
      try {
        this.errorMessage.set(await this.languageService.translate(errorMessage))
      } catch (e) {
        console.error(e)
        // Fallback in case of i18n issues
        this.errorMessage.set(
          'An error occurred while signing in. Please try again or contact support.',
        )
      }
    }
  }

  protected prepareUsername(username: string) {
    return username
  }

  protected trackLogin() {
    this.api.myProfile.trackLogin().catch((e) => console.error(`Cannot track login`, e))
  }

  protected checkIsUserActive() {
    return this.api.myProfile.isActive()
  }
}
